import type { CompanyType } from "@/types/company";
import type { ItemType } from "@/types/item";
import { createContext, useContext, useEffect, useState } from "react";

const dummyItems: ItemType[] = [
  {
    id: "1",
    name: "Item 1",
    image: "https://via.placeholder.com/150",
    companyName: "Company 1",
    companyId: "1",
    categoryName: "Category 1",
    price: 10,
    description: "Description 1",
  },
  {
    id: "2",
    name: "Item 2",
    image: "https://via.placeholder.com/150",
    companyName: "Company 2",
    companyId: "2",
    categoryName: "Category 2",
    price: 20,
    description: "Description 2",
  },
];
const dummyCompanies: CompanyType[] = [
  {
    id: "1",
    name: "GCP",
    image:
      "https://admin.kaman.rest/storage/logos/yCENmmiNRkgdLOvXk0qJn0isFklvVXffA3hVT07U.png",
    address: "Address 1",
    workingHours: [
      [0, 0], // Sunday closed
      [900, 1700], // Monday
      [900, 1700], // Tuesday
      [900, 1700], // Wednesday
      [900, 1700], // Thursday
      [900, 2200], // Friday
      [1000, 2200], // Saturday
    ],
  },
  {
    id: "2",
    name: "Awtar",
    image:
      "https://admin.kaman.rest/storage/logos/rHRmHMkV7scIvmSiGXOCv2xMFpe1hQt614w0u0i2.jpg",
    address: "Address 2",
    workingHours: [
      [0, 0], // Sunday closed
      [900, 1700], // Monday
      [900, 1700], // Tuesday
      [900, 1400], // Wednesday
      [900, 1700], // Thursday
      [900, 2200], // Friday
      [1000, 2200], // Saturday
    ],
  },
];

interface FavouritesContextType {
  favouriteItems: ItemType[];
  favouriteCompanies: CompanyType[];
  addFavouriteItem: (item: ItemType) => void;
  addFavouriteCompany: (company: CompanyType) => void;
  removeFavouriteItem: (item: ItemType) => void;
  removeFavouriteCompany: (restaurant: CompanyType) => void;
  isFavouriteItem: (itemId: string) => boolean;
  isFavouriteCompany: (companyId: string) => boolean;
}

const favouritesContext = createContext<FavouritesContextType | undefined>(
  undefined
);

export const FavouritesProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [favouriteItems, setFavouriteItems] = useState<ItemType[]>(
    JSON.parse(localStorage.getItem("favouriteItems") || "[]")
  );
  const [favouriteCompanies, setFavouriteCompanies] = useState<CompanyType[]>(
    JSON.parse(localStorage.getItem("favouriteCompanies") || "[]")
  );

  const isFavouriteCompany = (companyId: string) => {
    return favouriteCompanies.find((c) => c.id === companyId) !== undefined;
  };

  const isFavouriteItem = (itemId: string) => {
    return favouriteItems.find((i) => i.id === itemId) !== undefined;
  };

  useEffect(() => {
    localStorage.setItem("favouriteItems", JSON.stringify(favouriteItems));
    localStorage.setItem(
      "favouriteCompanies",
      JSON.stringify(favouriteCompanies)
    );
  }, [favouriteItems, favouriteCompanies]);

  const addFavouriteItem = (item: ItemType) => {
    console.log("adding favourite item: ", item);
    setFavouriteItems([...favouriteItems, item]);
  };

  const addFavouriteCompany = (company: CompanyType) => {
    setFavouriteCompanies([...favouriteCompanies, company]);
  };

  const removeFavouriteItem = (item: ItemType) => {
    setFavouriteItems(favouriteItems.filter((i) => i.id !== item.id));
  };

  const removeFavouriteCompany = (company: CompanyType) => {
    setFavouriteCompanies(
      favouriteCompanies.filter((c) => c.id !== company.id)
    );
  };

  return (
    <favouritesContext.Provider
      value={{
        favouriteItems,
        favouriteCompanies,
        addFavouriteItem,
        addFavouriteCompany,
        removeFavouriteItem,
        removeFavouriteCompany,
        isFavouriteItem,
        isFavouriteCompany,
      }}
    >
      {children}
    </favouritesContext.Provider>
  );
};

export const useFavourites = () => {
  const context = useContext(favouritesContext);
  if (!context) {
    throw new Error("useFavourites must be used within a FavouritesProvider");
  }
  return context;
};
